import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, redirectToIdentityLogin } from '../contexts/AuthContext';

/**
 * /auth/callback — receives tokens from the Identity Platform and completes login.
 *
 * Identity Platform redirects here with ?access_token=X&refresh_token=Y.
 * We store both in memory (via setTokens), fetch the Vault user + page rights,
 * then navigate to /.
 */
export default function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setTokens } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const access = params.get('access_token');
    const refresh = params.get('refresh_token');

    if (!access) {
      setError('Geen toegangstoken ontvangen van Identity Platform');
      return;
    }

    let cancelled = false;
    (async () => {
      const ok = await setTokens(access, refresh);
      if (cancelled) return;
      if (ok) {
        navigate('/', { replace: true });
      } else {
        setError('Kon sessie niet starten');
      }
    })();
    return () => { cancelled = true; };
  }, [params, setTokens, navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center login-animated-bg"
      data-testid="auth-callback-page"
    >
      <style>{`
        @keyframes loginGradient {
          0%,100% { background-position: 0% 0%; }
          50% { background-position: 100% 100%; }
        }
        .login-animated-bg {
          background: linear-gradient(135deg, #0D1117, #161B22, #1B2A6B, #0D1117);
          background-size: 400% 400%;
          animation: loginGradient 8s ease infinite;
        }
      `}</style>
      <div
        className="rounded-2xl p-10 text-center border border-white/[0.06]"
        style={{ background: 'rgba(22, 27, 34, 0.75)', backdropFilter: 'blur(16px)' }}
      >
        {error ? (
          <>
            <p className="text-sm text-red-400 mb-3" data-testid="auth-callback-error">{error}</p>
            <button
              onClick={() => redirectToIdentityLogin()}
              className="px-4 py-2 rounded-lg bg-[#2E75B6] text-white text-sm"
              data-testid="auth-callback-retry"
            >
              Opnieuw inloggen
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-[#E6EDF3]">Sessie wordt gestart...</p>
            <div className="mt-4 mx-auto w-5 h-5 rounded-full border-2 border-[#3B82F6]/40 border-t-[#3B82F6] animate-spin" />
          </>
        )}
      </div>
    </div>
  );
}
